import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useGet, usePost} from "@/apiHooks/HttpFunctions";
import RenderToast from "@/components/RenderToast";
import {Col, Row} from "react-bootstrap";
import BasicInput from "@/components/BasicInput";
import BasicModal from "@/Modals/BasicModal";
import {useFormik} from "formik";
import classes from "@/Modals/BaseModal.module.css";
import Input from "@/components/Input/Input";
import useFormApiState from "@/CustomHooks/useFormApiState";
import getAdminAdjustmentSchema from "@/Modals/AdminAdjustmentAction/schema";
import {webUsersApi} from "@/apis/apis";
import PlatformFeeChooser from "@/pages/UserManagement/PlatformFeeChooser";
import getAdjustFeeSchema from "@/Modals/UserPlatformFeeModal/schema";


function UserPlatformFeeModal({
  headerText,
  userId,
  isOpen,
  onCancel,
  onSuccess,
}) {
  const [platformFees, setPlatformFees] = useState([]);
  const initialFormData = useMemo(() => ({
    platform_fees: platformFees,
    attachments: [],
  }), [platformFees]);
  const [formData, setFormData] = useState(initialFormData);

  const { data: feesData, error: showError } = useGet({
    apiURL: `${webUsersApi}/${userId}/platform-fees`,
    enabled: !!userId,
  });

  useEffect(() => {
    if (feesData?.data) {
      setPlatformFees(feesData.data);
    }
  }, [feesData]);

  const platformFeePost = usePost({
    apiURL: `${webUsersApi}/${userId}/platform-fees`,
    isFormData: true,
    onSuccess: (data) => onSubmitSuccess(data.message, onSuccess),
    onError: (error) => {
      RenderToast({type: "error", message: error.response.data.errors.error ?? "Something went wrong."});
    },
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [platformFeePost.isPending],
    [platformFeePost.error]
  );

  const handleSubmit = async (values) => {
    const formData = new FormData();
    for (let file of values["attachments"]) {
      formData.append("attachments[]", file);
    }
    for (const valueKey in values["platform_fees"]) {
      let fee = values["platform_fees"][valueKey];
      let formKey = `platform_fees[${valueKey}]`;

      formData.append(`${formKey}[currency]`, fee.currency_code);
      formData.append(`${formKey}[type]`, fee.amount_type);
      formData.append(`${formKey}[amount]`, fee.amount);
      formData.append(`${formKey}[enabled]`, Number(fee.enabled));
    }

    platformFeePost.mutate(formData);
  };

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: getAdjustFeeSchema(),
    initialValues: formData,
    onSubmit: handleSubmit,
  });

  const onSubmitSuccess = useCallback((message, callback = null) => {
    setFormData(initialFormData);
    formik.resetForm();
    RenderToast({type: "success", message: message ?? "Success"});
    if (callback) {
      callback();
    }
  }, [formik, initialFormData]);

  const onFileChange = useCallback((e) => {
    formik.setFieldValue("attachments", e.target.files);
  }, [formik]);

  const getInputLabel = useCallback((fee) => {
    const type = fee.amount_type === 'fixed' ? 'Fixed' : 'Percentage';
    return `${type} Fee (${fee.currency_code})`
  }, []);
  const onFeeChange = useCallback((fee) => {
    setPlatformFees((prev) => {
      const prevFeeIndex = prev.findIndex(f => f.currency_code === fee.currency_code);
      prev[prevFeeIndex] = fee;

      formik.setFieldValue("platform_fees", prev);
      return prev;
    });
  }, [formik]);

  return (
    <BasicModal
      header={headerText}
      show={isOpen}
      onHide={onCancel}
      saveLoading={saveIsPending}
      formik={formik}
      saveLabel={"Save"}
    >
      {platformFees.map((fee, index) => <Row key={index} className="mb-2">
        <Col md={12}>
          <PlatformFeeChooser
            platformFee={fee}
            fieldName={`platform_fees[${index}]`}
            saveError={saveError}
            label={getInputLabel(fee)}
            formik={formik}
            onChange={onFeeChange}/>
        </Col>
      </Row>)}
      <hr />
      <Row>
        <Col md={12}>
          <p className="mb-1 font-bold">Please enter details for this action:</p>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <Input
            type="file"
            value={formik.values.attachments}
            onChange={onFileChange}
            labelClass={classes.label}
            label="Attachments"
            className={classes.inputDiv}
            inputContainerClass={classes.input}
            errorTextClass={classes.errorText}
            multiple
            accept="image/*, application/pdf"
          />
        </Col>
      </Row>
    </BasicModal>
  );
}

export default UserPlatformFeeModal;
